import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { AuthProvider, useAuth, authApi } from "@/lib/auth";
import LiveTV from "@/pages/LiveTV";
import Watch from "@/pages/Watch";
import Browse from "@/pages/Browse";
import Search from "@/pages/Search";
import Login from "@/pages/Login";
import MyList from "@/pages/MyList";
import Admin from "@/pages/Admin";
import News from "@/pages/News";
import NewsDetail from "@/pages/NewsDetail";
import TvGuide from "@/pages/TvGuide";
import Books from "@/pages/Books";
import Advertise from "@/pages/Advertise";
import Premium from "@/pages/Premium";
import PurchaseChannel from "@/pages/PurchaseChannel";
import MyChannel from "@/pages/MyChannel";
import GiftPremium from "@/pages/GiftPremium";
import RedeemGift from "@/pages/RedeemGift";
import BulkGift from "@/pages/BulkGift";
import MinistryPortal from "@/pages/MinistryPortal";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Navbar from "@/components/Navbar";
import { Toaster } from "sonner";

function AuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return; ran.current = true;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/", { replace: true }); return; }
    (async () => {
      try {
        const { data } = await authApi.post("/auth/emergent/callback", { session_id: m[1] });
        setUser(data); window.history.replaceState(null, "", "/"); nav("/", { replace: true });
      } catch { nav("/login?error=oauth", { replace: true }); }
    })();
    // eslint-disable-next-line
  }, []);
  return <div className="pt-24 text-center text-white/60 font-heading text-xl">Completing sign-in…</div>;
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="pt-24 text-center text-white/60">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <><Navbar /><AuthCallback /></>;
  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/" element={<LiveTV />} />
        <Route path="/live" element={<LiveTV />} />
        <Route path="/live/:slug" element={<LiveTV />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/watch/:id" element={<Watch />} />
        <Route path="/search" element={<Search />} />
        <Route path="/tv-guide" element={<TvGuide />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:id" element={<NewsDetail />} />
        <Route path="/books" element={<Books />} />
        <Route path="/advertise" element={<Advertise />} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/gift-premium" element={<GiftPremium />} />
        <Route path="/redeem-gift" element={<RedeemGift />} />
        <Route path="/bulk-gift" element={<BulkGift />} />
        <Route path="/ministry" element={<ProtectedRoute><MinistryPortal /></ProtectedRoute>} />
        <Route path="/purchase-channel" element={<ProtectedRoute><PurchaseChannel /></ProtectedRoute>} />
        <Route path="/my-channel" element={<ProtectedRoute><MyChannel /></ProtectedRoute>} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/my-list" element={<ProtectedRoute><MyList /></ProtectedRoute>} />
        <Route path="/admin/*" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="min-h-screen bg-[#050507] text-white grain" data-testid="app-root">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
